import { useEffect, useState, type FormEvent } from 'react';

// ---------------------------------------------------------------------------
// "Find a loan" — the search-everything entry point.
//
// Separate from the filter bar by design. The two controls answer two different
// questions: the filter bar asks "which of these loans do I care about", this
// asks "where is the loan I was handed". Running the second through the first
// meant clearing your own default chip before you could search, which put a
// step of undo in front of the task that needs to be quickest.
//
// Submitting drops the active filters (the caller's job) so nothing can hide
// the loan you asked for.
// ---------------------------------------------------------------------------

type Props = {
  /** The applied search term, '' when not searching. */
  active: string;
  onSearch: (term: string) => void;
  /** Drops the search and restores the caller's default filters. */
  onClear: () => void;
  /** Rows matched by the search, and the size of the pool it searched. */
  matched: number;
  total: number;
};

export function FindBar({ active, onSearch, onClear, matched, total }: Props) {
  const [draft, setDraft] = useState(active);

  // Re-sync when the search is cleared from somewhere else — "Reset to default",
  // or the clear button below. Keyed on `active` so it doesn't fight typing.
  useEffect(() => { setDraft(active); }, [active]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const term = draft.trim();
    if (term) onSearch(term);
  };

  return (
    <form className="find-bar" onSubmit={submit}>
      <span className="find-label">Find a loan</span>
      <input
        type="search"
        className="find-input"
        placeholder="SBA loan #, UMWSB #, or borrower name"
        value={draft}
        onChange={e => setDraft(e.target.value)}
      />
      <button type="submit" className="btn-sm btn-primary" disabled={!draft.trim()}>
        Search all loans
      </button>
      {active && (
        <button type="button" className="btn-sm" onClick={onClear}>← Back</button>
      )}

      {active && (
        <span className="find-status">
          {matched === 0
            ? <>No loans match <strong>{active}</strong></>
            : <>{matched} of {total} loans match <strong>{active}</strong></>}
        </span>
      )}
    </form>
  );
}
